import { Link } from "@shared/client/navigation";
import { ArrowUpRight } from "lucide-react";
import { Badge } from "@shared/components/ui/badge";
import { Card } from "@shared/components/ui/card";
import { LEAD_STATUSES, type LeadStatus } from "@shared/lib/leads-shared";
import { useT } from "@shared/client/locale";

/**
 * Le pont qui manquait entre un rapport et le pipeline : un passage produit
 * des leads, on doit pouvoir passer de l'un à l'autre. Sans ce lien, le
 * rapport et la table de leads racontaient deux fois la même chose sans jamais
 * se répondre.
 */
export function PassLeads({
  leads,
  runAt,
}: {
  leads: Array<{ id: string; name: string; status: string; first_seen_at: string }>;
  runAt: string;
}) {
  const t = useT();
  if (leads.length === 0) return null;

  const boundary = Date.parse(runAt);

  return (
    <Card aria-label={t.dashboard.passLeads.title} className="mt-8 gap-0 overflow-hidden py-0">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b px-5 py-3.5">
        <p className="m-0 text-[12px] font-medium text-muted-foreground">
          {t.dashboard.passLeads.title}
        </p>
        <Link
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-muted-foreground transition-colors hover:text-foreground"
          href="/app/leads"
        >
          {t.dashboard.passLeads.openPipeline} <ArrowUpRight size={13} aria-hidden="true" />
        </Link>
      </div>
      <ul className="m-0 flex list-none flex-wrap gap-2 p-5">
        {leads.map((lead) => {
          const isNew = Date.parse(lead.first_seen_at) >= boundary;
          return (
            <li key={lead.id}>
              <Link
                className="inline-flex items-center gap-2 rounded-md border px-2.5 py-1 text-[12.5px] font-medium hover:bg-muted"
                href={`/app/leads?lead=${lead.id}`}
              >
                {isNew && <span aria-hidden="true" className="size-1.5 rounded-full bg-primary" />}
                {lead.name}
                <Badge className="font-normal text-muted-foreground" variant="outline">
                  {(LEAD_STATUSES as readonly string[]).includes(lead.status)
                    ? t.shell.leadStatuses[lead.status as LeadStatus]
                    : lead.status}
                </Badge>
              </Link>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
